import { createFileRoute, Outlet } from '@tanstack/react-router';
import { seo } from '~/util';
import { bountiesQuery } from '~/util/queries/public/bounties';
import { BountyPost } from './-BountyPost';
import type { Bounty, BountyDifficulty } from '~/util/types/bounties';
import { useMemo, useRef, useState } from 'react';
import { ScrollArea } from '~/primitives/scroll-area';
import { useScopes } from '~/components/Providers/AuthContext';
import { Button } from '~/primitives/button';
import { ChevronDown, Plus } from 'lucide-react';
import { NewBountySheet } from './-NewBountySheet';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '~/primitives/collapsible';
import { useScrollIntoViewOnMobile } from '~/util/hooks/useScrollIntoViewOnMobile';
import { compareBountiesByDefault, isBountyActive } from './-bountySort';

export const Route = createFileRoute('/games/bounty-board')({
    loaderDeps: ({ search: { list } }) => ({
        list,
    }),
    beforeLoad: async ({ context: { queryClient } }) => {
        const [classic, platformer] = await Promise.all([
            queryClient.fetchQuery(
                bountiesQuery({
                    list: 'classic',
                }),
            ),
            queryClient.fetchQuery(
                bountiesQuery({
                    list: 'platformer',
                }),
            ),
        ]);

        return { classic, platformer };
    },
    loader: async ({ deps: { list }, context: { classic, platformer } }) =>
        list === 'classic' ? classic : platformer,
    head: () => ({
        meta: seo({
            title: 'Bounty Board',
        }),
    }),
    component: BountyBoard,
});

function BountyBoard() {
    const [isCreateBountyOpen, setIsCreateBountyOpen] = useState(false);
    const bounties = Route.useLoaderData();
    const infoRef = useRef<HTMLDivElement>(null);
    const scrollToInfo = useScrollIntoViewOnMobile(infoRef);

    const bountiesByDifficulty = useMemo(() => {
        const now = new Date();
        const sortedBounties = [...bounties].sort((a, b) =>
            compareBountiesByDefault(a, b, now),
        );

        return sortedBounties.reduce((acc, bounty) => {
            const currentDifficutly = acc.get(bounty.bounty_difficulty) || {
                active: [],
                inactive: [],
            };
            currentDifficutly[
                isBountyActive(bounty, now) ? 'active' : 'inactive'
            ].push(bounty);
            acc.set(bounty.bounty_difficulty, currentDifficutly);
            return acc;
        }, new Map<BountyDifficulty, Record<'active' | 'inactive', Bounty[]>>());
    }, [bounties]);

    const { hasScope } = useScopes();

    return (
        <div className="mt-8 lg:mt-10 flex items-start justify-center">
            <div
                className="flex w-full h-[85vh] gap-4 overflow-x-scroll snap-x snap-mandatory lg:overflow-x-hidden lg:snap-none"
                style={{ WebkitOverflowScrolling: 'touch' }}
            >
                <div className="shrink-0 w-screen snap-start lg:shrink lg:basis-2/3 lg:min-w-0 lg:w-auto lg:snap-none bg-background-dark rounded-xl clamp-[p-2-8-clamp] h-full flex flex-col items-center">
                    <div className="flex gap-2 items-center mb-2">
                        <h2 className="text-center text-secondary-foreground clamp-[text-xl-3xl-clamp] font-bold">
                            BOUNTY BOARD
                        </h2>
                        {hasScope('bounty_manage') && (
                            <Button onClick={() => setIsCreateBountyOpen(true)}>
                                <Plus className="mr-2" />
                                Post bounty
                            </Button>
                        )}
                    </div>
                    <div className="flex justify-center border rounded-xl w-full flex-1 min-h-0">
                        {(['Easy', 'Medium', 'Hard'] as BountyDifficulty[]).map(
                            (difficulty) => (
                                <div
                                    key={difficulty}
                                    className="pr-1 py-2 flex flex-col flex-1 basis-0 min-w-0 first:border-none border-l"
                                >
                                    <h3 className="text-center clamp-[text-sm-2xl-clamp] font-semibold uppercase">
                                        {difficulty}
                                    </h3>
                                    <ScrollArea
                                        className="w-full flex-1 min-h-0"
                                        viewportClassName="pt-2 pl-0 [scrollbar-gutter:stable] px-3"
                                    >
                                        <div className="flex flex-col w-full gap-4">
                                            {(bountiesByDifficulty.get(
                                                difficulty,
                                            )?.active.length ?? 0 > 0) ? (
                                                bountiesByDifficulty
                                                    .get(difficulty)
                                                    ?.active.map((bounty) => (
                                                        <BountyPost
                                                            key={bounty.id}
                                                            bounty={bounty}
                                                            onBountySelect={
                                                                scrollToInfo
                                                            }
                                                        />
                                                    ))
                                            ) : (
                                                <div className="flex items-center justify-center text-center text-secondary-light w-full p-2 rounded-xl border-2 h-28 clamp-[text-xs-base-clamp]">
                                                    <p>
                                                        There are currently no
                                                        bounties of this
                                                        difficulty.
                                                    </p>
                                                </div>
                                            )}
                                            <Collapsible className="w-full">
                                                <CollapsibleTrigger className="group">
                                                    <h3 className="clamp-[text-sm-xl-clamp] font-semibold uppercase">
                                                        Previous Bounties
                                                    </h3>
                                                    <ChevronDown className="w-6 h-6 transition-transform text-primary group-data-[state=open]:rotate-180" />
                                                </CollapsibleTrigger>
                                                <CollapsibleContent contentClassName="p-0">
                                                    <div className="flex flex-col w-full gap-2">
                                                        {bountiesByDifficulty
                                                            .get(difficulty)
                                                            ?.inactive.map(
                                                                (bounty) => (
                                                                    <BountyPost
                                                                        key={
                                                                            bounty.id
                                                                        }
                                                                        bounty={
                                                                            bounty
                                                                        }
                                                                        onBountySelect={
                                                                            scrollToInfo
                                                                        }
                                                                    />
                                                                ),
                                                            )}
                                                    </div>
                                                </CollapsibleContent>
                                            </Collapsible>
                                        </div>
                                    </ScrollArea>
                                </div>
                            ),
                        )}
                    </div>
                </div>
                <div
                    ref={infoRef}
                    className="shrink-0 w-screen snap-start lg:shrink lg:basis-1/3 lg:min-w-0 lg:w-auto lg:snap-none bg-background-dark rounded-xl clamp-[p-2-2-clamp]"
                >
                    <Outlet />
                </div>
            </div>
            {hasScope('bounty_manage') && (
                <NewBountySheet
                    open={isCreateBountyOpen}
                    onOpenChange={setIsCreateBountyOpen}
                />
            )}
        </div>
    );
}
